
import { useState } from 'react';
import { useUser, useFirestore, updateDocumentNonBlocking } from '@/firebase';
import { doc, arrayUnion } from 'firebase/firestore';
import { Button } from '@/components/ui/button';
import { BookMarked, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import type { Loch, LoggedSwim } from '@/app/lib/types';
import { cn } from '@/lib/utils';

interface LogSwimButtonProps {
  location: Loch;
}

export function LogSwimButton({ location }: LogSwimButtonProps) {
  const { user, isUserLoading } = useUser();
  const firestore = useFirestore();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [isOpen, setIsOpen] = useState(false);
  const [date, setDate] = useState<Date | undefined>(new Date());
  const [notes, setNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const handleLogSwim = async () => {
    if (!user || !firestore) {
      navigate('/login');
      return;
    }
    if (!date) {
      toast({ variant: 'destructive', title: 'Please select a date.' });
      return;
    }

    setIsSaving(true);
    const userProfileRef = doc(firestore, 'users', user.uid);

    const newSwimLog: LoggedSwim = {
      locationId: location.id,
      locationName: location.name,
      locationRegion: location.region,
      locationImageId: location.imageId,
      date: date.toISOString(),
      notes: notes,
    };

    try {
      // Using updateDocumentNonBlocking - fire and forget
      updateDocumentNonBlocking(userProfileRef, {
        loggedSwims: arrayUnion(newSwimLog)
      });
      
      toast({
        title: 'Swim Logged!',
        description: `Your swim at ${location.name} has been saved.`,
      });

      // Reset form and close dialog
      setIsOpen(false);
      setDate(new Date());
      setNotes('');
    } catch (error) {
        // This catch block might not be hit if update is non-blocking,
        // but it's good practice for other potential errors.
      console.error("Error logging swim:", error);
      toast({ variant: 'destructive', title: 'Failed to log swim.' });
    } finally {
      setIsSaving(false);
    }
  };

   if (!user && !isUserLoading) {
    return (
       <Button variant="outline" onClick={() => navigate('/login')}>
        <BookMarked className="mr-2 h-4 w-4" />
        Log a Swim
      </Button>
    )
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <BookMarked className="mr-2 h-4 w-4" />
          Log a Swim
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Log Your Swim at {location.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <Popover>
            <PopoverTrigger asChild>
                <Button
                variant={"outline"}
                className={cn(
                    "w-full justify-start text-left font-normal",
                    !date && "text-muted-foreground"
                )}
                >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {date ? format(date, "PPP") : <span>Pick a date</span>}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
                <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(d) => d > new Date() || d < new Date("2000-01-01")}
                    initialFocus
                />
            </PopoverContent>
          </Popover>
          <Textarea
            placeholder="How was the water? Any interesting sights?"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={4}
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setIsOpen(false)}>Cancel</Button>
          <Button onClick={handleLogSwim} disabled={isSaving}>
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Swim
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
